import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import { apiErrorMessage } from "utils/errors/parseApiError";

const MIN_PASSWORD_LENGTH = 8;

export interface UseChangePasswordFormReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  handleSubmit: () => Promise<void>;
  handleCloseError: () => void;
}

export function useChangePasswordForm(): UseChangePasswordFormReturn {
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const confirmRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowLoading(false);
    setShowError(false);
    setErrorMsg("");
  };

  const handleSubmit = async () => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      openError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      openError("Passwords do not match. Please try again.");
      return;
    }

    try {
      setLoadingMsg("Updating password…");
      setShowLoading(true);
      await authService.changePassword(newPassword);

      navigate("/", { replace: true });
    } catch (err: any) {
      openError(
        apiErrorMessage(err, "Failed to update password. Please try again."),
      );
    }
  };

  const focusConfirm = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") confirmRef.current?.focus();
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "newPassword",
      label: "New Password",
      type: "password",
      value: newPassword,
      onChange: setNewPassword,
      onKeyDown: focusConfirm,
      placeholder: "Enter your new password",
      autoComplete: "new-password",
    },
    {
      name: "confirmPassword",
      label: "Confirm New Password",
      type: "password",
      value: confirmPassword,
      onChange: setConfirmPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Confirm your new password",
      autoComplete: "new-password",
      inputRef: confirmRef,
    },
  ];

  return {
    fields,
    submitRef,
    isSubmitDisabled: !newPassword || !confirmPassword,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    handleSubmit,
    handleCloseError,
  };
}
